import type { CombinationStake } from "@/features/betslip/types";
import type {
  BetslipActions,
  BetslipStore,
  SliceCreator,
} from "@/features/betslip/stores/types";

export const initialSubmissionState: Pick<
  BetslipStore,
  | "submission"
  | "selectionResults"
  | "multiLegRejections"
  | "multiResult"
  | "transportError"
  | "alternativeStakeReferences"
  | "multiAlternativeStakeReference"
> = {
  submission: { phase: "idle" },
  selectionResults: {},
  multiLegRejections: {},
  multiResult: null,
  transportError: null,
  alternativeStakeReferences: {},
  multiAlternativeStakeReference: null,
};

type SubmissionAuxKeys =
  | "selectionResults"
  | "multiLegRejections"
  | "alternativeStakeReferences";

/** A copy of `map` without `key`. Store deltas are new objects, never mutated. */
export const omitKey = <V>(
  map: Record<string, V>,
  key: string,
): Record<string, V> => {
  const { [key]: _dropped, ...rest } = map;
  return rest;
};

/** Drop per-outcomeId submission aux entries (results, alt-stake refs). */
export const pruneSubmissionByOutcomeIds = (
  state: BetslipStore,
  drop: ReadonlySet<string>,
): Pick<BetslipStore, SubmissionAuxKeys> => {
  const nextResults: BetslipStore["selectionResults"] = {};
  for (const [outcomeId, result] of Object.entries(state.selectionResults)) {
    if (!drop.has(outcomeId)) nextResults[outcomeId] = result;
  }
  const nextLegRejections: BetslipStore["multiLegRejections"] = {};
  for (const [outcomeId, rejection] of Object.entries(
    state.multiLegRejections,
  )) {
    if (!drop.has(outcomeId)) nextLegRejections[outcomeId] = rejection;
  }
  const nextRefs: BetslipStore["alternativeStakeReferences"] = {};
  for (const [outcomeId, ref] of Object.entries(
    state.alternativeStakeReferences,
  )) {
    if (!drop.has(outcomeId)) nextRefs[outcomeId] = ref;
  }
  return {
    selectionResults: nextResults,
    multiLegRejections: nextLegRejections,
    alternativeStakeReferences: nextRefs,
  };
};

/**
 * Delta to invalidate any prior `multiAlternativeStakeReference` when
 * the multi composition changes (e.g. a new selection is added). The
 * alt-stake-ref is tied to a specific multi shape — once that shape
 * changes, the reference no longer applies.
 *
 * Owned by the submission slice so the rule is discoverable from
 * here, not buried inside `selections.addSelection`.
 */
export const invalidateMultiAltStakeRef = (): Pick<
  BetslipStore,
  "multiAlternativeStakeReference"
> => ({
  multiAlternativeStakeReference: null,
});

/**
 * Bounce back to `idle` when the slip composition changes while the
 * user is at the "Confirm Bet" prompt. The bet they were about to
 * confirm isn't the bet they're now looking at, so they need to
 * re-press Place Bet. Returns an empty delta otherwise. Mutating slice
 * actions spread this in front of their own delta.
 */
export const exitConfirmingIfMutated = (
  state: BetslipStore,
): Pick<BetslipStore, "submission"> | Record<string, never> =>
  state.submission.phase === "confirming"
    ? { submission: { phase: "idle" } }
    : {};

/** Every submission-axis field back to initial, connection alert included. */
export const resetSubmissionState = (): Pick<
  BetslipStore,
  | "selectionResults"
  | "multiLegRejections"
  | "multiResult"
  | "transportError"
  | "alternativeStakeReferences"
  | "multiAlternativeStakeReference"
> => ({
  selectionResults: {},
  multiLegRejections: {},
  multiResult: null,
  transportError: null,
  alternativeStakeReferences: {},
  multiAlternativeStakeReference: null,
});

export const resetCombo = (combo: CombinationStake): CombinationStake => ({
  ...combo,
  total: 0,
  perCombo: 0,
  isPromo: false,
  betId: undefined,
  entryId: undefined,
});

/**
 * Re-arm the slip after an edit without opening a double-bet path. Anything
 * accepted — single, multi or combo - hands back its result and zeroes its
 * stake, so the next submit can't re-send a bet that already placed. Cards the
 * user is still working on keep their stakes and their own alerts.
 */
export const clearAcceptedForEdit = (
  state: BetslipStore,
): Pick<BetslipStore, "selectionResults" | "multiResult" | "stakes"> => {
  const nextResults: BetslipStore["selectionResults"] = {};
  const nextSingles = { ...state.stakes.singles };

  for (const [outcomeId, result] of Object.entries(state.selectionResults)) {
    if (result.status === "accepted") {
      nextSingles[outcomeId] = 0;
    } else {
      nextResults[outcomeId] = result;
    }
  }

  const nextCombinations: BetslipStore["stakes"]["combinations"] = {};
  for (const [type, combo] of Object.entries(state.stakes.combinations)) {
    nextCombinations[type] =
      combo.betId !== undefined ? resetCombo(combo) : combo;
  }

  const multiPlaced = state.multiResult?.status === "accepted";

  return {
    selectionResults: nextResults,
    multiResult: multiPlaced ? null : state.multiResult,
    stakes: {
      ...state.stakes,
      singles: nextSingles,
      multi: multiPlaced ? 0 : state.stakes.multi,
      combinations: nextCombinations,
    },
  };
};

/**
 * Reset a placed slip to an editable re-bet of the same selections.
 *
 * `oddsToCommit` defaults to every pending change on purpose: a re-bet starts
 * from the current market, and stakes are zeroed here, so the user re-stakes
 * against the price they can see. Callers that mean "commit exactly these
 * prices" (a card's Accept, the multi's Update, Accept All under
 * AUTO_HIGHER) pass their own scoped map so the rest stay pending.
 */
export const reuseSelections = (
  state: BetslipStore,
  selectionsToReuse: BetslipStore["selections"] = state.selections,
  oddsToCommit: BetslipStore["oddsChanges"] = state.oddsChanges,
) => {
  // Write the given pending prices into selection.odds so the user can
  // re-submit at them without an extra confirm step.
  const nextSelections = { ...selectionsToReuse };
  for (const [outcomeId, newOdds] of Object.entries(oddsToCommit)) {
    if (newOdds === undefined) continue;
    const selection = nextSelections[outcomeId];
    if (selection) nextSelections[outcomeId] = { ...selection, odds: newOdds };
  }

  // A change the caller chose not to commit (a price drop AUTO_HIGHER won't
  // take) stays pending rather than vanishing.
  const remainingChanges: BetslipStore["oddsChanges"] = {};
  for (const [outcomeId, newOdds] of Object.entries(state.oddsChanges)) {
    if (newOdds === undefined || oddsToCommit[outcomeId] !== undefined)
      continue;
    remainingChanges[outcomeId] = newOdds;
  }

  // Zero out stakes — caller drives the next bet from a clean slate.
  const zeroedSingles: Record<string, number> = {};
  for (const outcomeId of Object.keys(nextSelections)) {
    zeroedSingles[outcomeId] = 0;
  }

  return {
    selections: nextSelections,
    oddsChanges: remainingChanges,
    stakes: {
      ...state.stakes,
      singles: zeroedSingles,
      multi: 0,
      combinations: {},
    },
    // The re-bet starts funding-neutral: Bonus Cash toggles reset so
    // promo balance isn't silently re-committed.
    promoCash: { singles: {}, multi: false },
    // Reuse Bets clears the connection alert too — the failed submit it
    // belonged to is being replaced.
    ...resetSubmissionState(),
  };
};

type SubmissionActions = Pick<
  BetslipActions,
  | "placeBet"
  | "confirmBet"
  | "cancelConfirm"
  | "responsesProcessed"
  | "setSelectionResult"
  | "setMultiLegRejection"
  | "clearMultiLegRejection"
  | "setMultiResult"
  | "clearResults"
  | "dismissSelectionResult"
  | "dismissMultiResult"
  | "setTransportError"
  | "dismissTransportError"
  | "setAlternativeStakeReference"
  | "setMultiAlternativeStakeReference"
  | "reuseSelections"
>;

/**
 * Submission phase state machine. Phase is one of `idle`, `confirming`,
 * `submitting`. Invalid (state, event) pairs leave the phase unchanged
 * so out-of-order events don't throw.
 *
 *   idle        --PLACE_BET-->            confirming
 *   confirming  --BACK------>             idle
 *   confirming  --CONFIRM--->             submitting
 *   confirming  --slip mutation-->        idle    (see exitConfirmingIfMutated)
 *   submitting  --RESPONSES_PROCESSED-->  idle
 *
 * The "review" UI state is not a phase. It's derived from
 * `selectionResults` / `multiResult` / combos in `computed.ts`.
 */
export const submissionSlice: SliceCreator<
  typeof initialSubmissionState & SubmissionActions
> = (set) => ({
  ...initialSubmissionState,

  placeBet: () => {
    set((s) =>
      s.submission.phase === "idle"
        ? { submission: { phase: "confirming" } }
        : {},
    );
  },

  confirmBet: () => {
    set((s) =>
      s.submission.phase === "confirming"
        ? { submission: { phase: "submitting" } }
        : {},
    );
  },

  cancelConfirm: () => {
    set((s) =>
      s.submission.phase === "confirming"
        ? { submission: { phase: "idle" } }
        : {},
    );
  },

  responsesProcessed: () => {
    set((s) =>
      s.submission.phase === "submitting"
        ? { submission: { phase: "idle" } }
        : {},
    );
  },

  setSelectionResult: (outcomeId, result) => {
    set((state) => ({
      selectionResults: {
        ...state.selectionResults,
        [outcomeId]: result,
      },
    }));
  },

  setMultiLegRejection: (outcomeId, rejection) => {
    set((state) => ({
      multiLegRejections: {
        ...state.multiLegRejections,
        [outcomeId]: rejection,
      },
    }));
  },

  clearMultiLegRejection: (outcomeId) => {
    set((state) => {
      if (state.submission.phase === "submitting") return state;
      return {
        multiLegRejections: omitKey(state.multiLegRejections, outcomeId),
      };
    });
  },

  setMultiResult: (result) => {
    set({ multiResult: result });
  },

  clearResults: () => {
    set(resetSubmissionState());
  },

  // Only the dismissed banner goes. A receipt takes its stake with it, since
  // the bet already exists and an armed stake would re-send it. A rejection
  // keeps its stake - that's the bet being fixed.
  dismissSelectionResult: (outcomeId) => {
    set((state) => {
      if (state.submission.phase === "submitting") return state;
      const result = state.selectionResults[outcomeId];
      if (!result) return state;
      return {
        selectionResults: omitKey(state.selectionResults, outcomeId),
        stakes:
          result.status === "accepted"
            ? {
                ...state.stakes,
                singles: { ...state.stakes.singles, [outcomeId]: 0 },
              }
            : state.stakes,
      };
    });
  },

  dismissMultiResult: () => {
    set((state) => {
      if (state.submission.phase === "submitting") return state;
      return {
        multiResult: null,
        stakes:
          state.multiResult?.status === "accepted"
            ? { ...state.stakes, multi: 0 }
            : state.stakes,
      };
    });
  },

  setTransportError: (alert) => {
    set({ transportError: alert });
  },

  dismissTransportError: () => {
    set({ transportError: null });
  },

  setAlternativeStakeReference: (outcomeId, betId) => {
    set((state) => ({
      alternativeStakeReferences: {
        ...state.alternativeStakeReferences,
        [outcomeId]: betId,
      },
    }));
  },

  setMultiAlternativeStakeReference: (betId) => {
    set({ multiAlternativeStakeReference: betId });
  },

  reuseSelections: () => {
    set((state) => reuseSelections(state));
  },
});
